import type { RouteRecordRaw } from 'vue-router'

import { authRoutes } from './auth.routes.js'

/** Named so `safeRedirect` can refuse to send a sign-in back to a page that does not exist. */
export const NOT_FOUND_ROUTE = 'not-found'

export const routes: readonly RouteRecordRaw[] = [
  {
    path: '/',
    name: 'overview',
    component: () => import('@/views/overview/OverviewView.vue'),
    // The landing screen for every operator. No `requires`: the backend returns each block
    // only for the permission it needs, so the dashboard shows what the screens would.
    meta: { layout: 'app', title: 'Dashboard', crumb: 'Dashboard' },
  },

  ...authRoutes,

  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/account/AccountView.vue'),
    // No `requires`: an operator's own account is theirs. The backend agrees — the route
    // carries `@AdminRoute()` with no permission.
    meta: { layout: 'app', title: 'My account', crumb: 'My account' },
  },

  // ── Merchants
  {
    path: '/shops',
    name: 'shops',
    component: () => import('@/views/shop/ShopsView.vue'),
    meta: { layout: 'app', title: 'Shops', crumb: 'Shops', requires: ['shop-view'] },
  },
  {
    path: '/shops/:id',
    name: 'shop',
    component: () => import('@/views/shop/ShopDetailView.vue'),
    // The credential panel inside is gated separately, on `shop-credential-view`.
    meta: { layout: 'app', title: 'Shop', crumb: 'Shop', parent: 'shops', requires: ['shop-view'] },
  },

  // ── Promotions
  {
    path: '/promotions',
    name: 'promotions',
    component: () => import('@/views/promotion/PromotionsView.vue'),
    meta: { layout: 'app', title: 'Promotions', crumb: 'Promotions', requires: ['promotion-view'] },
  },
  {
    path: '/promotions/:id',
    name: 'promotion',
    component: () => import('@/views/promotion/PromotionDetailView.vue'),
    meta: { layout: 'app', title: 'Promotion', crumb: 'Promotion', parent: 'promotions', requires: ['promotion-view'] },
  },

  // ── Revenue and analytics
  {
    path: '/revenue',
    name: 'revenue',
    component: () => import('@/views/revenue/RevenueView.vue'),
    meta: { layout: 'app', title: 'Revenue', crumb: 'Revenue', requires: ['revenue-view'] },
  },
  {
    path: '/revenue/:shopId',
    name: 'revenue-shop',
    component: () => import('@/views/revenue/RevenueDetailView.vue'),
    meta: { layout: 'app', title: 'Shop revenue', crumb: 'Shop revenue', parent: 'revenue', requires: ['revenue-view'] },
  },
  {
    path: '/analytics/health',
    name: 'analytics-health',
    component: () => import('@/views/analytics/AnalyticsHealthView.vue'),
    meta: { layout: 'app', title: 'Analytics health', crumb: 'Analytics health', requires: ['analytics-view'] },
  },

  {
    path: '/reports/usage',
    name: 'reports-usage',
    component: () => import('@/views/report/UsageReportView.vue'),
    meta: { layout: 'app', title: 'Usage', crumb: 'Usage', requires: ['analytics-view'] },
  },

  {
    path: '/apps',
    name: 'apps',
    component: () => import('@/views/app-catalog/AppsView.vue'),
    meta: { layout: 'app', title: 'Apps', crumb: 'Apps', requires: ['app-view'] },
  },
  {
    path: '/shopify-functions',
    name: 'shopify-functions',
    component: () => import('@/views/shopify-function/ShopifyFunctionsView.vue'),
    meta: { layout: 'app', title: 'Shopify Functions', crumb: 'Shopify Functions', requires: ['shopify-function-view'] },
  },

  // ── Widgets and billing
  {
    path: '/widgets',
    name: 'widgets',
    component: () => import('@/views/widget/WidgetsView.vue'),
    meta: { layout: 'app', title: 'Widgets', crumb: 'Widgets', requires: ['widget-view'] },
  },
  {
    path: '/plans',
    name: 'plans',
    component: () => import('@/views/plan/PlansView.vue'),
    meta: { layout: 'app', title: 'Plans', crumb: 'Plans', requires: ['plan-view'] },
  },
  {
    path: '/subscriptions',
    name: 'subscriptions',
    component: () => import('@/views/subscription/SubscriptionsView.vue'),
    meta: { layout: 'app', title: 'Subscriptions', crumb: 'Subscriptions', requires: ['subscription-view'] },
  },

  // ── Admin: the self-administration slice. Each route names the permission its backend
  //    counterpart carries in `@AdminRoute('…')`; `requirePermissions` enforces it.
  {
    path: '/operators',
    name: 'operators',
    component: () => import('@/views/operator/OperatorsView.vue'),
    meta: { layout: 'app', title: 'Operators', crumb: 'Operators', requires: ['admin-user-view'] },
  },
  {
    path: '/roles',
    name: 'roles',
    component: () => import('@/views/role/RolesView.vue'),
    meta: { layout: 'app', title: 'Roles', crumb: 'Roles', requires: ['role-view'] },
  },
  {
    path: '/audit-log',
    name: 'audit-log',
    component: () => import('@/views/audit/AuditLogView.vue'),
    meta: { layout: 'app', title: 'Audit log', crumb: 'Audit log', requires: ['audit-log-view'] },
  },
  {
    path: '/shop-activity',
    name: 'shop-events',
    component: () => import('@/views/shop-event/ShopEventsView.vue'),
    meta: {
      layout: 'app',
      title: 'Shop activity',
      crumb: 'Shop activity',
      requires: ['shop-event-view'],
    },
  },

  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/system/ForbiddenView.vue'),
    // Reachable by any signed-in operator: it is where `requirePermissions` sends someone
    // who lacks a permission, so gating it would be a redirect loop.
    meta: { layout: 'app', title: 'Not permitted' },
  },

  {
    path: '/:pathMatch(.*)*',
    name: NOT_FOUND_ROUTE,
    component: () => import('@/views/system/NotFoundView.vue'),
    // Public: a signed-out visitor following a dead link should see "no such page", not be
    // bounced to sign-in and then to a page that still does not exist.
    meta: { public: true, layout: 'auth', title: 'Not found' },
  },
]
